import React, { createContext, useContext, useCallback, useMemo, useState } from "react";

const STORAGE_KEY = "mj_dev_mode";

// Build-time flag: only the dev (DOGMA QA) build leaves VITE_DEV_MODE on.
// Anywhere else ('false') dev mode is forced off — the navbar toggle is hidden
// there too, so a stale localStorage value would otherwise be unescapable.
const devModeAllowed = import.meta.env.VITE_DEV_MODE !== "false";

// Where allowed, dev mode defaults on and the navbar switch lets any user
// flip it at runtime per browser, overriding the default via localStorage.
function getInitialDevMode(): boolean {
  if (!devModeAllowed) return false;
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === "true";
  }
  return true;
}

// Plain (non-hook) read of the current dev-mode flag, straight off
// localStorage — for the handful of plain utility/async functions (not
// components/hooks) that need to gate dev-mode-only behaviour but can't call
// `useDevMode()` (e.g. calculateSumAnalysisGrandTotal, called from both
// hooks and boqVersionUtils.ts's plain async save functions). Always reads
// the live current value, never a stale snapshot.
export const isDevModeEnabled = getInitialDevMode;

interface DevModeContextType {
  devMode: boolean;
  setDevMode: (value: boolean) => void;
  toggleDevMode: () => void;
}

const DevModeContext = createContext<DevModeContextType | null>(null);

export const useDevModeContext = (): DevModeContextType => {
  const context = useContext(DevModeContext);
  if (context === null) {
    throw new Error("useDevModeContext must be used within a DevModeProvider");
  }
  return context;
};

// Convenience hook for the common case of just reading the flag - this is
// the direct drop-in replacement for the old `const showDevMode = import.meta.env.VITE_DEV_MODE !== 'false'`
// module-level constants scattered across takeoff/bill-summary components.
export const useDevMode = (): boolean => useDevModeContext().devMode;

export const DevModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [devMode, setDevModeState] = useState<boolean>(getInitialDevMode);

  const setDevMode = useCallback((value: boolean) => {
    setDevModeState(value);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, String(value));
    }
  }, []);

  const toggleDevMode = useCallback(() => {
    setDevMode(!devMode);
  }, [devMode, setDevMode]);

  const value = useMemo(
    () => ({ devMode, setDevMode, toggleDevMode }),
    [devMode, setDevMode, toggleDevMode],
  );

  return (
    <DevModeContext.Provider value={value}>{children}</DevModeContext.Provider>
  );
};
